import React, { useEffect, useState, useRef, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, FormControl, InputGroup, Row, ProgressBar } from 'react-bootstrap';
import _ from 'lodash';

import 'leaflet/dist/leaflet.css';
import { MainPageApi } from './main-page.api';
import './main-page.scss';
import { FirebaseHelper } from '../api';
import { CoordinatesHelper, EmergencyHelper } from '../helpers';
import { STORE } from '../redux-configs';
import {
    ACTIONS,
    addToAllEmergenciesActionCreator,
    initializeAllEmergenciesActionCreator,
    setLocationsActionCreator,
    setPathsActionCreator,
    setSearchTextActionCreator,
    setShowFinishedActionCreator,
    updateEmergencyFromAllEmergenciesActionCreator as updateEmergencyActionCreator,
} from '../actions';
import {
    DataTable,
    DetailsTable,
    MapComponent,
    NavigationBar,
} from '../building-blocks';
import NewEmergencyModal from '../new-emergency-modal/new-emergency-modal';
import WelcomeBackModal from '../welcome-back-modal/welcome-back-modal';
import useWindowDimensions from '../hooks/use-window-dimensions';
import { setShowTestActionCreator } from '../actions/set-show-test.action';

function MainPage() {
    const { width, height } = useWindowDimensions();

    const mainApi = MainPageApi.getInstance();
    const backendURL = process.env.NX_BACKEND_URL;
    const backendPort = process.env.NX_BACKEND_PORT;

    const dispatch = useDispatch();
    const {
        allEmergencies,
        newEmergencies,
        selectedEmergency,
        filters: { search },
    } = useSelector(STORE);

    const audioRef = useRef(null);
    const [map, setMap] = useState(null);
    const [loading, setLoading] = useState(false);
    const [newEmergencyShow, setNewEmergencyShow] = useState(false);
    const [showFinished, setShowFinished] = useState(false);
    const [showTest, setShowTest] = useState(false);
    const [showWelcomeBack, setShowWelcomeBack] = useState(true);
    const [showStepsModal, setShowStepsModal] = useState(false);

    useEffect(() => {
        map?.invalidateSize();
    }, [map, selectedEmergency, width, height]);

    const fetchEmergencies = useCallback(async () => {
        try {
            setLoading(true);
            const emergencies = await mainApi.fetchEmergencies();
            dispatch(
                initializeAllEmergenciesActionCreator(
                    emergencies.map((e) =>
                        EmergencyHelper.arrangeEmergencyFields(e)
                    )
                )
            );
            setLoading(false);
        } catch (e) {
            console.error('Something went wrong during fetching emergencies', e);
            // TODO: uncomment after full refactoring
            // alert('Error! Please, contact tech support!');
        }
    }, [dispatch, mainApi]);

    useEffect(() => {
        fetchEmergencies().then();
    }, [fetchEmergencies]);

    useEffect(() => {
        FirebaseHelper.fetchData('colorPaths')
            .then((paths) => {
                return paths.map((p) => {
                    const { color, coord, name } = p.data();
                    let coordinates = coord;
                    coordinates = CoordinatesHelper.normalizeCoord(coordinates);
                    coordinates = CoordinatesHelper.toArrayWrapper(coordinates);
                    coordinates = CoordinatesHelper.parseToArray(coordinates);
                    coordinates.map((c) => c.reverse());
                    return { coordinates, name, color };
                });
            })
            .then((normalized) => {
                dispatch(setPathsActionCreator(normalized));
            });
    }, [dispatch]);

    useEffect(() => {
        FirebaseHelper.fetchData('locations')
            .then((paths) => {
                return paths.map((p) => {
                    const { coord, name, type, desc, address } = p.data();
                    const coordinates = JSON.parse(
                        coord.replace(/\s/g, '')
                    ).reverse();
                    return { coordinates, name, type, desc, address };
                });
            })
            .then((normalized) => {
                dispatch(setLocationsActionCreator(normalized));
            });
    }, [dispatch]);

    useEffect(() => {
        const eventSource = new EventSource(
            `${backendURL}:${backendPort}/v1/sse?auth=${
                JSON.parse(localStorage.getItem('user-token'))['accessToken']
            }`
        );
        eventSource.onmessage = ({ data }) => {
            if (data === 'keep-alive') {
                return;
            }
            const parsedData = JSON.parse(data);
            if (!_.isEmpty(parsedData)) {
                const { messageType, ...newEmergencyFromAPI } = parsedData;
                if (messageType === 'new-emergency') {
                    setNewEmergencyShow(true);
                    audioRef?.current?.play();

                    const transformedEmergency =
                        EmergencyHelper.arrangeEmergencyFields(
                            newEmergencyFromAPI
                        );
                    dispatch({
                        type: ACTIONS.ADD_TO_NEW_EMERGENCIES,
                        payload: transformedEmergency,
                    });
                    dispatch(
                        addToAllEmergenciesActionCreator(transformedEmergency)
                    );
                } else if (['processing-started', 'processing-finished'].includes(messageType)) {
                    dispatch({
                        type: ACTIONS.REMOVE_FROM_NEW_EMERGENCIES,
                        payload: newEmergencyFromAPI,
                    });
                    fetchEmergencies().then();
                }
            }
        };
    }, [backendPort, backendURL, dispatch, fetchEmergencies]);

    useEffect(() => {
        if (!newEmergencies?.length) {
            audioRef?.current?.pause();
        }
    }, [newEmergencies?.length]);

    const toggleFinished = () => {
        setShowFinished(!showFinished);
        dispatch(setShowFinishedActionCreator(!showFinished));
        window.scrollTo(0, 0);
    };

    const toggleTest = () => {
        setShowTest(!showTest);
        dispatch(setShowTestActionCreator(!showTest));
        window.scrollTo(0, 0);
    };

    const onSearchChanged = (updatedSearch: string) => {
        dispatch(setSearchTextActionCreator(updatedSearch));
    };

    const handleSkip = async () => {
        setNewEmergencyShow(newEmergencies.length > 1);
        await mainApi.pushEmergencyLog(
            allEmergencies.find((e) => newEmergencies[0].id === e.id),
            'SKIPPED'
        );

        dispatch({
            type: ACTIONS.REMOVE_FROM_NEW_EMERGENCIES,
            payload: newEmergencies[0],
        });
    };

    const handleProcess = async () => {
        setNewEmergencyShow(newEmergencies.length > 1);
        if (newEmergencies.length === 1) {
            audioRef?.current?.pause();
        }

        const log = await mainApi.pushEmergencyLog(newEmergencies[0], 'PROCESSING');
        await mainApi.updateEmergencyStatus(newEmergencies[0]?.id, 'inProgress');

        const emergency = {
            ...newEmergencies[0],
            logs: [...(newEmergencies[0].logs ?? []), log],
            status: 'inProgress'
        };

        dispatch(updateEmergencyActionCreator(emergency));
        dispatch({
            type: ACTIONS.SET_SELECTED_EMERGENCY,
            payload: emergency,
        });

        setShowStepsModal(true);
    };

    return (
        <>
            {loading && <ProgressBar striped now={100} animated className="main-progress" />}
            <Container fluid className="main-container">
                <audio
                    ref={audioRef}
                    loop={true}
                    src={`${backendURL}:${backendPort}/noti.mp3`}
                />
                <NavigationBar />
                {width > 800 && (
                    <Row className="row-60">
                        <MapComponent
                            styles={{ width: selectedEmergency ? '65%' : '100%' }}
                            setMap={setMap}
                        />
                        {selectedEmergency && (
                            <DetailsTable
                                setShowSteps={setShowStepsModal}
                                showSteps={showStepsModal}
                            />
                        )}
                    </Row>
                )}
                {width <= 800 && (
                    <>
                        <Row className="row-75">
                            <MapComponent
                                styles={{ width: '100%' }}
                                setMap={setMap}
                            />
                        </Row>
                        {selectedEmergency && (
                            <DetailsTable
                                setShowSteps={setShowStepsModal}
                                showSteps={showStepsModal}
                            />
                        )}
                    </>
                )}
                <div className="row m-0">
                    <div className="col-12 data-table-pre-header mt-3">
                        <div className="checkbox-container">
                            <div className="finished-checkbox">
                                <input
                                    type="checkbox"
                                    id="finished"
                                    defaultChecked={showFinished}
                                    onChange={() => toggleFinished()}
                                />
                                <label htmlFor="finished">
                                    Показувати завершені
                                </label>
                            </div>
                            {showFinished && (
                                <div className="test-checkbox">
                                    <input
                                        type="checkbox"
                                        id="test"
                                        defaultChecked={showTest}
                                        onChange={() => toggleTest()}
                                    />
                                    <label htmlFor="test">
                                        Показувати хибні
                                    </label>
                                </div>
                            )}
                        </div>
                        <InputGroup className="search-input">
                            <InputGroup.Text id="basic-addon1">
                                Пошук
                            </InputGroup.Text>
                            <FormControl
                                onChange={(e) =>
                                    onSearchChanged(e.target.value)
                                }
                                value={search ?? ''}
                                aria-describedby="basic-addon1"
                                type="search"
                            />
                        </InputGroup>
                    </div>
                </div>
                <Row className="data-row mx-0">
                    <DataTable map={map} />
                </Row>
            </Container>
            {newEmergencies[0] && (
                <NewEmergencyModal
                    show={newEmergencyShow}
                    total={newEmergencies.length}
                    emergency={newEmergencies[0]}
                    handleSkip={handleSkip}
                    handleProcess={handleProcess}
                />
            )}
            {showWelcomeBack && (
                <WelcomeBackModal
                    show={showWelcomeBack}
                    setShow={setShowWelcomeBack}
                />
            )}
        </>
    );
}

export default withRouter(MainPage);
